/**
 * @fileoverview Full-screen photo viewer for violation occurrence attachments.
 * @module inspection-renderer/violations
 *
 * Responsibilities:
 * - Present a single attachment (or a swipeable set) full-screen, optimized
 *   for touch devices (iPad/tablet inspectors) — large tap targets, dark
 *   backdrop, pinch-friendly `object-contain` sizing.
 * - Resolve the attachment id to a displayable URL via the host-injected
 *   `ThumbnailResolver` (same contract used by `AttachmentThumb`).
 *
 * Dependencies:
 * - React 18 (peer).
 * - `useThumbnailResolver` from `../contracts/rendererContext`.
 * - lucide-react (X, ChevronLeft, ChevronRight).
 *
 * Architecture:
 * - Self-contained, no portal — matches `TypeYesConfirmModal` / `OccurrenceRepairModal`
 *   convention of fixed-inset + high z-index rendered inline.
 *
 * Notes:
 * - Escape closes; ArrowLeft/ArrowRight navigate when multiple photos are open.
 * - Backdrop click closes; clicks on the image/controls do not propagate.
 *
 * @component PhotoLightbox
 *
 * @contract PhotoLightboxProps
 * Purpose: prop contract for the full-screen photo viewer.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import type { AttachmentRefLike } from '@firesquirrel/core-domain';
import { useThumbnailResolver } from '../contracts/rendererContext';

export interface PhotoLightboxProps {
  /** The full set of attachments this lightbox can navigate between. */
  attachments: AttachmentRefLike[];
  /** Index within `attachments` to open on. */
  initialIndex: number;
  onClose: () => void;
}

export function PhotoLightbox({ attachments, initialIndex, onClose }: PhotoLightboxProps): ReactNode {
  const resolver = useThumbnailResolver();
  const [index, setIndex] = useState(Math.min(Math.max(initialIndex, 0), attachments.length - 1));
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const current = attachments[index];
  const hasMultiple = attachments.length > 1;

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setUrl(null);
    if (!resolver || !current) {
      setLoading(false);
      return;
    }
    resolver(current._id).then((resolved) => {
      if (alive) {
        setUrl(resolved);
        setLoading(false);
      }
    }).catch(() => {
      if (alive) {
        setUrl(null);
        setLoading(false);
      }
    });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolver, current?._id]);

  useEffect(() => {
    function handler(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') setIndex((i) => Math.max(0, i - 1));
      if (e.key === 'ArrowRight') setIndex((i) => Math.min(attachments.length - 1, i + 1));
    }
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose, attachments.length]);

  if (!current) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-modal="photo-lightbox"
      className="fixed inset-0 z-200 flex items-center justify-center bg-black/90 backdrop-blur-xs"
      onClick={onClose}
    >
      {/* Close */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        aria-label="Close"
        data-action="lightbox-close"
        className="absolute top-4 right-4 sm:top-6 sm:right-6 z-10 p-2.5 rounded-full bg-white/10 text-white hover:bg-white/20 active:scale-95 transition-all"
      >
        <X size={22} />
      </button>

      {/* Prev */}
      {hasMultiple && index > 0 && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setIndex((i) => i - 1); }}
          aria-label="Previous photo"
          data-action="lightbox-prev"
          className="absolute left-2 sm:left-6 z-10 p-3 rounded-full bg-white/10 text-white hover:bg-white/20 active:scale-95 transition-all"
        >
          <ChevronLeft size={26} />
        </button>
      )}

      {/* Next */}
      {hasMultiple && index < attachments.length - 1 && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setIndex((i) => i + 1); }}
          aria-label="Next photo"
          data-action="lightbox-next"
          className="absolute right-2 sm:right-6 z-10 p-3 rounded-full bg-white/10 text-white hover:bg-white/20 active:scale-95 transition-all"
        >
          <ChevronRight size={26} />
        </button>
      )}

      {/* Image stage */}
      <div
        className="max-w-[94vw] max-h-[88vh] flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        {loading ? (
          <div className="w-14 h-14 rounded-full border-4 border-white/25 border-t-white animate-spin" />
        ) : url ? (
          <img
            src={url}
            alt={current.fileName ?? `Photo ${index + 1}`}
            data-lightbox-image="true"
            className="max-w-full max-h-[88vh] object-contain rounded-xl shadow-2xl select-none"
          />
        ) : (
          <div className="text-white/70 text-sm px-4 py-3 bg-white/5 rounded-lg">Photo unavailable</div>
        )}
      </div>

      {/* Counter */}
      {hasMultiple && (
        <div className="absolute bottom-5 left-1/2 -translate-x-1/2 text-white/80 text-xs font-semibold bg-white/10 px-3 py-1 rounded-full">
          {index + 1} / {attachments.length}
        </div>
      )}
    </div>
  );
}
